import { forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { t } from 'i18next';

interface MonitoringLaptimesChartProps {
    data: number[];
}

export const MonitoringLaptimesChart = forwardRef<ReactECharts, MonitoringLaptimesChartProps>((props, ref) => {
    const categories = props.data.map((_, index) => `${t("Lap")} ${index + 1}`);

    // Calcular tiempos acumulados
    const accumulatedTimes = props.data.reduce((acc: number[], time, index) => {
        const accumulated = index === 0 ? time : acc[index - 1] + time;
        acc.push(accumulated);
        return acc;
    }, []);

    // Calcular promedio, máximo y mínimo
    const avg = props.data.length > 0
        ? props.data.reduce((sum, val) => sum + val, 0) / props.data.length
        : 0;
    const max = props.data.length > 0 ? Math.max(...props.data) : 0;
    const min = props.data.length > 0 ? Math.min(...props.data) : 0;

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const millis = Math.round((seconds % 1) * 1000);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
    };

    const options = {
        title: {
            text: t('Lap times'),
            left: 'center'
        },
        tooltip: {
            trigger: 'axis',
            formatter: (params: any) => {
                const barParam = params.find((p: any) => p.seriesType === 'bar');
                if (!barParam) return '';

                const lapIndex = barParam.dataIndex;
                const lapTime = props.data[lapIndex];
                const accumulatedTime = accumulatedTimes[lapIndex];

                let result = `${barParam.name}<br/>`;
                result += `${barParam.marker}${t('Lap time')}: ${formatTime(lapTime)}<br/>`;
                result += `<span style="color:#5470c6">⏱</span> ${t('Accumulated')}: ${formatTime(accumulatedTime)}<br/>`;
                result += `<hr style="margin: 5px 0; border: none; border-top: 1px solid #ccc;"/>`;
                result += `<span style="color:#91cc75">●</span> ${t('Average')}: ${formatTime(avg)}<br/>`;
                result += `<span style="color:#ee6666">●</span> ${t('Maximum')}: ${formatTime(max)}<br/>`;
                result += `<span style="color:#fac858">●</span> ${t('Minimum')}: ${formatTime(min)}`;
                return result;
            }
        },
        legend: {
            data: [t('Time')],
            top: '10%'
        },
        xAxis: {
            type: 'category',
            data: categories
        },
        yAxis: {
            type: 'value',
            name: t('Time'),
            min: 0,
            max: (value: any) => Math.ceil(value.max + 2),
            axisLabel: {
                formatter: (value: number) => formatTime(value)
            },
            splitLine: {
                show: true,
                lineStyle: {
                    type: 'dotted',
                    color: '#e0e0e0'
                }
            }
        },
        series: [
            {
                name: t('Time'),
                data: props.data,
                type: 'bar',
                color: '#5470c6',
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params: any) => {
                        const lapTime = params.data;
                        const accumulatedTime = accumulatedTimes[params.dataIndex];
                        return `${formatTime(lapTime)}\n(${formatTime(accumulatedTime)})`;
                    },
                    fontSize: 10,
                    lineHeight: 14
                },
                markLine: {
                    symbol: 'none',
                    label: {
                        show: true,
                        position: 'insideEndTop',
                        formatter: (params: any) => {
                            const value = params.value;
                            return formatTime(value);
                        }
                    },
                    data: [
                        {
                            name: t('Maximum'),
                            yAxis: max,
                            lineStyle: {
                                color: '#ee6666',
                                type: 'dashed',
                                width: 2
                            },
                            label: {
                                color: '#ee6666',
                                fontWeight: 'bold',
                                formatter: (params: any) => `${t('Maximum')}: ${formatTime(params.value)}`
                            }
                        },
                        {
                            name: t('Average'),
                            yAxis: avg,
                            lineStyle: {
                                color: '#91cc75',
                                type: 'dashed',
                                width: 2
                            },
                            label: {
                                color: '#91cc75',
                                fontWeight: 'bold',
                                formatter: (params: any) => `${t('Average')}: ${formatTime(params.value)}`
                            }
                        },
                        {
                            name: t('Minimum'),
                            yAxis: min,
                            lineStyle: {
                                color: '#fac858',
                                type: 'dashed',
                                width: 2
                            },
                            label: {
                                color: '#fac858',
                                fontWeight: 'bold',
                                formatter: (params: any) => `${t('Minimum')}: ${formatTime(params.value)}`
                            }
                        }
                    ]
                }
            }
        ],
        grid: {
            left: '10%',
            right: '10%',
            bottom: '10%',
            top: '20%'
        }
    };

    return (
        <div className="monitoring-chart">
            <ReactECharts ref={ref} option={options} style={{ height: '300px' }} />
        </div>
    );
});
